
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/hooks/auth';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  // Add debug logging
  console.log("[ProtectedAdminRoute] Auth state:", { 
    user: user?.email, 
    isAdmin, 
    loading,
    userId: user?.id
  });

  // Show loading indicator while checking auth status
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Loading authentication status...</p>
      </div>
    </div>;
  }
  
  // User is not logged in, redirect to login
  if (!user) {
    console.log("[ProtectedAdminRoute] No user found, redirecting to login");
    return <Navigate to="/login" replace />;
  }
  
  // User is logged in but is not an admin
  if (!isAdmin) {
    console.log("[ProtectedAdminRoute] User is not an admin:", user.email);
    return (
      <div className="container py-10 flex flex-col items-center justify-center min-h-[80vh]">
        <Alert variant="warning" className="max-w-md">
          <AlertTitle className="text-xl">Access Denied</AlertTitle>
          <AlertDescription>
            You don't have admin privileges to access this area.
            <div className="mt-4">
              <Navigate to="/" replace />
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  // User is logged in and is an admin, render the protected content
  console.log("[ProtectedAdminRoute] Admin access granted for:", user.email);
  return <>{children}</>;
};

export default ProtectedAdminRoute;
